import {
  BreadCrumb,
  Dictionary, DrupalDocumentMedia,
  DrupalProductNode, DrupalProductVariationGroup,
  ProductClassTaxonomyTerm,
  ProductTypeTaxonomyTerm
} from "@/types/hygiena-types";
import {useContext, useEffect, useRef, useState} from "react";
import {DictionaryContext} from "@/context/dictionary-context";
import {getProductVariations} from "@/lib/get-product-variations";
import {buildBreadCrumbs} from "@/lib/breadcrumbs";
import classNames from "classnames";
import {absoluteURL} from "@/lib/absolute-url";
import {Link} from "@/components/atoms/link";
import ProductImages from "../widget--product-images";
import parse from "html-react-parser";
import VariationSelect from "../widget--variation-select";
import {NodeProductDocumentButtonGrid} from "../widget--document-button-grid";
import {MediaImage} from "@/components/templates/media/media--image";
import {HERO_IMAGE_STYLE} from "@/types/image-styles";
import {HERO_IMAGE_SIZES, HERO_MAX_WIDTH, PlaceHolderHeroImage} from "../hero";
import {FrostedGlassContainer} from "@/components/atoms/gradient-container";
import {BreadCrumbs} from "@/components/molecules/breadcrumbs";
import {getDocumentUri} from "@/lib/get-document-uri";
import {LucideInfo} from "lucide-react";
import RenderProductNumber from "@/components/molecules/product-numbers";

/**
 * The technology tooltip.
 * @param description
 */
function TechnologyTooltip({description}: {description: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <LucideInfo onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} className="cursor-pointer" size={17}/>
      <div className={`absolute bg-white drop-shadow transition-opacity bottom-10 p-4 [&>*]:text-sm${!open ? ' hidden opacity-0' : ' opacity-100'} `}>{parse(description)}</div>
    </>
  )
}

/**
 * Product Page Hero.
 *
 * @param taxonomy_term
 *   A Product Type/Product Name taxonomy term.
 * @param node
 *   The product node.
 * @constructor
 */
export function ProductHero({
                              taxonomy_term,
                              node,
                              openModal,
                              openModalContent,
                              openSmallModal,
                              openSmallModalContent,
                            }: {
  taxonomy_term?: ProductTypeTaxonomyTerm | ProductClassTaxonomyTerm,
  openModal?: Function | null,
  openModalContent?: Function | null,
  openSmallModal?: Function | null,
  openSmallModalContent?: Function | null,
  node: DrupalProductNode
}) {
  // @todo this is fine for now, but let's merge this ProductHero with the productType hero.
  const t = useContext<Dictionary>(DictionaryContext);
  const [beginVideo, setBeginVideo] = useState<boolean>(false);
  const variations = getProductVariations(node);
  const breadcrumbs = [] as BreadCrumb[];
  const backgroundImage = getBackgroundImage();
  /// New Variations.
  const [activeVariant, setActiveVariant] = useState<DrupalProductVariationGroup | null>(variations?.[0] ?? null);
  /// End New Variations.
  const [smallScreen, setSmallScreen] = useState<boolean>();
  const videoRef = useRef<HTMLDivElement | null>(null);
  const heroVideo = node?.field_product_hero_video;
  const certificates = node?.field_product_certificates?.length
    ? node.field_product_certificates.filter(item => item?.field_certificate_type?.name !== undefined)
    : [];
  const certsNoFile = node?.field_product_certs_no_file?.filter(certificate => certificate?.name?.length) ?? [];
  const hasCategory = node?.field_product_category ?? false;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const setSize = () => setSmallScreen(window.innerWidth < 768);
    const setBegin = () => !beginVideo && setBeginVideo(true);
    setSize();
    window.addEventListener("resize", setSize);
    window.addEventListener("mousemove", setBegin);

    return () => {
      window.removeEventListener("resize", setSize);
      window.removeEventListener("mousemove", setBegin);
    }
  }, []);

  // We use the video URL if there is only one video being passed, if the video is an array, we
  // apply a fader.
  let videoFile;
  if (Array.isArray(heroVideo) && heroVideo?.length === 1) {
    videoFile = heroVideo?.[0]?.field_media_file;
  }
  else if (!Array.isArray(heroVideo) && heroVideo?.field_media_file) {
    videoFile = heroVideo.field_media_file;
  }

  const videoUrl = videoFile?.uri?.url
    ? absoluteURL(videoFile?.uri?.url)
    : undefined;
  let videoPoster = "";

  // Get a video blur uri if we have an image and a video.
  if (backgroundImage?.bf_image?.image_style_uri?.blur) {
    videoPoster = backgroundImage?.bf_image?.image_style_uri?.blur;
  }
  else if (backgroundImage?.image?.image_style_uri?.blur) {
    videoPoster = backgroundImage?.image?.image_style_uri?.blur;
  }

  function getBackgroundImage() {
    if (node?.field_product_hero_image && node?.field_product_hero_image?.type !== 'unknown') {
      return node?.field_product_hero_image;
    }
    else if (taxonomy_term?.field_product_type_image && taxonomy_term?.field_product_type_image?.type !== 'unknown') {
      return taxonomy_term?.field_product_type_image;
    }
    else if (taxonomy_term?.field_class_hero_image && taxonomy_term?.field_class_hero_image?.type !== 'unknown') {
      return taxonomy_term?.field_class_hero_image;
    }
    return null;
  }

  if (taxonomy_term?.parent) {
    buildBreadCrumbs(taxonomy_term, breadcrumbs);
  }

  /**
   * Formats the bracketed detail displayed next to certificates in the product header.
   *
   * @param item
   * @param spaces
   */
  function getCertificateDetail(item: DrupalDocumentMedia, spaces: boolean = true) {
    const parts: string[] = [];
    if (item?.field_document_cert_detail?.length) {
      parts.push(item.field_document_cert_detail);
    }
    if (item?.field_document_language?.[0]?.name && item.field_document_language[0].name !== 'English') {
      parts.push(item.field_document_language[0].name);
    }
    if (parts.length) {
      return `${spaces && ' '}(${parts.join(', ')})`;
    }
    return '';
  }

  function productHeroContent() {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-2">
        <ProductImages key={`product-hero-images--${node?.id}`} image_field={node?.field_product_image ?? []} video_field={node?.field_product_video} openModal={openModal} openModalContent={openModalContent} videoRef={videoRef}/>
        {/* Display copy beneath title if a Categorized product i.e. Salmonella. */}
        {hasCategory && (
          <div className="product-hero-body font-light md:hidden max-md:leading-body-lg">
            {node.body?.processed && parse(node.body.processed, {})}
          </div>
        )}
        <div className={classNames("text-black flex flex-col justify-between [&_p]:text-base", {"md:mt-12": hasCategory})}>
          <div>
            {/* Display copy to the right if the product doesn't use a category, but a product class instead. */}
            {!hasCategory && (
              <div className="product-hero-body font-light mb-4">
                {node.body?.processed && parse(node.body.processed, {})}
              </div>
            )}
            {node?.field_product_test_targets?.length ? (
              <div className="font-light mb-1">
                <label className="mr-1">{t?.all?.detection}:</label>
                {node?.field_product_test_targets && node.field_product_test_targets.map((item, key) => (
                  <span key={`test-targets--${item.id}--${key}`} className="inline [&>p]:inline">
                        {item?.field_test_target_display_title?.processed ? parse(item?.field_test_target_display_title?.processed, {}) : item.name}
                    {key !== node.field_product_test_targets.length - 1 && ", "}
                      </span>
                ))}
              </div>
            ): <></>}
            {node?.field_product_testing_method?.name?.length ?
              (
                <div
                  className="product-teaser--technology relative font-light mb-1 text-left flex gap-1 items-center">{t?.all?.technology}: {node.field_product_testing_method?.field_testing_method_link?.url?.length ? (
                  <Link className="text-primary hover:text-white font-bold"
                        href={node.field_product_testing_method.field_testing_method_link.url}>
                    {node.field_product_testing_method.name}
                  </Link>
                ) : node.field_product_testing_method.name}{node.field_product_testing_method?.description?.processed?.length ? (
                  <TechnologyTooltip description={node.field_product_testing_method.description.processed}/>
                ) : <></>}</div>
              ) : (<></>)
            }
            {/* Only display certificates if there are certificates set. */}
            {!node?.field_product_class && (certificates?.length || certsNoFile?.length) ? (
              <div className="font-light mb-2">
                <label className="mr-1">{certificates?.length > 1 ? `${t?.all?.certificates}` : `${t?.all?.certificate}`}:</label>
                {certificates?.length ? certificates.map((item, i) => (
                  <Link key={`certificates--${node.id}--${item.id}-${i}`} href={getDocumentUri(item) ?? "/"} target={"_blank"} className="hover:text-complimentary font-bold text-primary transition-all">
                    {item?.field_certificate_type?.name}{getCertificateDetail(item)}
                    <span className={"text-black"}>{(i !== certificates.length - 1 || certsNoFile?.length) ? ", " : ""}</span>
                  </Link>
                )): (!certsNoFile) ? "-" : ""}
                {certsNoFile?.length ? certsNoFile.map((item, i) => (
                  <span key={`certificates-with-no-file--${item.name}-${i}`} className="text-black">
                    {item?.name} {item?.refNo?.length ? `(${item.refNo})` : ''}
                    {i !== certsNoFile.length - 1 && ", "}
                  </span>
                )) : ""}
                {(!certificates?.length && !certsNoFile?.length) ? "-" : ""}
              </div>
            ) : <></>}
            <div className="font-light mb-2">
              <VariationSelect variations={variations} callback={setActiveVariant} hoverEffect={true} collapse={true} />
            </div>
            <div className="font-regular mb-4">
              {/* Categorized products display their product numbers beneath other fields i.e. Volume. */}
              {hasCategory && activeVariant && <RenderProductNumber variant={variations} singleLine={false} showSku={false} />}
            </div>
          </div>
          <div>
            {!hasCategory && activeVariant && (
              <div className="font-regular mb-4"><RenderProductNumber variant={variations} singleLine={false} showSku={false} /></div>
            )}
            <NodeProductDocumentButtonGrid node={node} contact={true} videoRef={videoRef} className={"!mb-0"} includeOrderOnline={true} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="relative md:min-h-[80vh] 2xl:max-w-[1920px] 2xl:mx-auto flex mb-6 md:mb-12">
        {videoUrl && (
          <div className="h-full w-full absolute">
            {backgroundImage && (
              <MediaImage media={backgroundImage}
                          loading={"eager"}
                          priority
                          imageStyle={HERO_IMAGE_STYLE}
                          key={`product-hero--${backgroundImage.id}`}
                          sizes={HERO_IMAGE_SIZES}
                          className={"absolute inset-0"} style={{objectFit: "cover", height: "100%", width: "100%", maxWidth: HERO_MAX_WIDTH, margin: "auto"}}/>
            )}
            {beginVideo && (
              <video muted loop autoPlay className="object-cover h-full w-full absolute max-md:hidden">
                <source src={videoUrl} type="video/mp4"/>
              </video>
            )}
          </div>
        )}
        {!videoUrl && backgroundImage ? (
          <MediaImage media={backgroundImage}
                      loading={"eager"}
                      priority
                      imageStyle={HERO_IMAGE_STYLE}
                      key={`product-hero--${backgroundImage.id}`}
                      sizes={HERO_IMAGE_SIZES}
                      className={"absolute inset-0"} style={{objectFit: "cover", height: "100%", width: "100%", maxWidth: HERO_MAX_WIDTH, margin: "auto"}}/>
        ) : (
          <>{!videoUrl && <PlaceHolderHeroImage altText={node?.title} />}</>
        )}
        <div className="container mx-auto py-20 min-h-fit flex justify-start items-center">
          <FrostedGlassContainer className="drop-shadow-sm px-6 md:px-12 py-8 rounded-md w-full z-10 max-md:text-center max-md:mx-6">
            {breadcrumbs && <BreadCrumbs breadcrumbs={breadcrumbs}/>}
            <div className="pb-3 text-black">
              <h1 className="pb-2 break-words !font-semibold md:!text-[36px] !mb-0">
                {node?.field_product_brand?.name && (
                  <span className="max-md:block">{node.field_product_brand.name}{node.field_product_brand?.field_brand_suffix && node.field_product_brand.field_brand_suffix}&nbsp;</span>
                )}
                {node?.field_product_display_title?.value ? parse(node?.field_product_display_title.value, {}) : node.title}
              </h1>
              {/* Display copy beneath title if a Categorized product i.e. Salmonella. */}
              {hasCategory && (
                <div className="product-hero-body font-light mb-4 max-md:hidden">
                  {node.body?.processed && parse(node.body.processed, {})}
                </div>
              )}
            </div>
            {!smallScreen && productHeroContent()}
          </FrostedGlassContainer>
        </div>
      </div>
      {smallScreen && (
        <div className="container">
          <div className="bg-white mx-6 drop-shadow-md p-8 mb-8">
            {productHeroContent()}
          </div>
        </div>
      )}
    </>
  )
}

